import React, { useContext } from "react";
import { HotelContext } from "../../context/HotelContext";
import { translate } from "../../utils/Translation";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, PieChart, Pie, CartesianGrid } from "recharts";

const ActionsSurveysSections = ({
  loading,
  error,
  reviews,
  quizzes,
  expandedQuizzes,
  setExpandedQuizzes,
  getSentimentColor,
  getSentimentImg,
  userIcon,
  mockReviewPeriodData,
  mockQuizPeriodData,
}) => {
  const { lang } = useContext(HotelContext);

  return (
    <div className="actions-surveys__content">
      <div className="actions-surveys__reviews-card">
        <h2 className="actions-surveys__card-title">{translate("v2_actions_surveys_reviews", lang)}</h2>
        <p className="actions-surveys__card-subtitle">{translate("v2_actions_surveys_reviews_sub", lang)}</p>

        {loading ? (
          <div className="actions-surveys__loading">{translate("v2_loading", lang)}</div>
        ) : error ? (
          <div className="actions-surveys__error">{translate("v2_error_load_data_plain", lang)}</div>
        ) : reviews.length === 0 ? (
          <div className="actions-surveys__empty">{translate("v2_actions_surveys_no_review", lang)}</div>
        ) : (
          <div className="actions-surveys__reviews-list">
            {reviews.map((review) => {
              const totalResponses = review.totalResponses || review.total || 0;
              const totalStays = review.totalStaysWithAction || 0;
              const participationPct = review.participationPercentage || 0;
              const sentiments = [4, 3, 2, 1].map((vote) => ({
                vote,
                color: getSentimentColor(vote),
                count: review.sentiment[vote].count,
                pct: review.sentiment[vote].percentage,
              }));
              return (
                <div key={review.actId} className="actions-surveys__review-card-item">
                  <div className="actions-surveys__review-item">
                    <div className="actions-surveys__review-row">
                      <div className="actions-surveys__review-left">
                        <h3 className="actions-surveys__review-name">{review.actionName}</h3>
                        <div className="actions-surveys__review-inline-stats">
                          {sentiments.map((s) => (
                            <div key={s.vote} className="actions-surveys__review-inline-chip">
                              <img src={getSentimentImg(s.vote)} alt={`sentiment-${s.vote}`} className="actions-surveys__sentiment-icon" />
                              <span className="actions-surveys__sentiment-count">
                                ({s.count}/{totalResponses}) {s.pct}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="actions-surveys__review-bar-wrapper">
                        <div className="actions-surveys__sentiment-bar-container">
                          <div className="actions-surveys__sentiment-emojis-above">
                            {sentiments.map((s, index) => {
                              if (s.count === 0) return null;
                              const cumulativeBefore = sentiments.slice(0, index).reduce((sum, prev) => sum + prev.pct, 0);
                              const position = cumulativeBefore + s.pct / 2;
                              return (
                                <div
                                  key={s.vote}
                                  className="actions-surveys__sentiment-emoji-above"
                                  style={{
                                    left: `${position}%`,
                                    transform: "translateX(-50%)",
                                  }}
                                >
                                  <img src={getSentimentImg(s.vote)} alt={`sentiment-${s.vote}`} className="actions-surveys__sentiment-emoji-img" />
                                </div>
                              );
                            })}
                          </div>
                          <div className="actions-surveys__sentiment-bar-global">
                            {sentiments.map((s) => (
                              <div
                                key={s.vote}
                                className="actions-surveys__sentiment-bar-segment"
                                style={{
                                  width: `${s.pct}%`,
                                  backgroundColor: s.color,
                                }}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="actions-surveys__review-participation">
                          <img src={userIcon} alt={translate("v2_surveys_user_alt", lang)} className="actions-surveys__review-participation-icon-img" />
                          <span className="actions-surveys__review-participation-text">
                            ({totalResponses}/{totalStays}) {participationPct}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="actions-surveys__quizzes-card">
        <h2 className="actions-surveys__card-title">{translate("v2_actions_surveys_quizzes", lang)}</h2>
        <p className="actions-surveys__card-subtitle">{translate("v2_actions_surveys_quizzes_sub", lang)}</p>

        {loading ? (
          <div className="actions-surveys__loading">{translate("v2_loading", lang)}</div>
        ) : error ? (
          <div className="actions-surveys__error">{translate("v2_error_load_data_plain", lang)}</div>
        ) : quizzes.length === 0 ? (
          <div className="actions-surveys__empty">{translate("v2_actions_surveys_no_quiz", lang)}</div>
        ) : (
          <div className="actions-surveys__quizzes-list">
            {quizzes.map((quiz) => {
              const isExpanded = expandedQuizzes[quiz.actId] === true;
              const totalResponses = quiz.totalResponses || quiz.total || 0;
              const totalStays = quiz.totalStaysWithAction || 0;
              const participationPct = quiz.participationPercentage || 0;
              const pieColors = ["#C68A6A", "#CFA7A0", "#F4EDDE"];
              const pieData = quiz.answers.map((answer, idx) => ({
                name: answer.text || translate("v2_surveys_answer_fallback", lang, { position: answer.position }),
                value: answer.timesChosen,
                percentage: answer.percentage,
                fill: pieColors[idx % pieColors.length],
              }));

              const toggleQuiz = () => {
                if (isExpanded) {
                  setExpandedQuizzes({});
                } else {
                  setExpandedQuizzes({ [quiz.actId]: true });
                }
              };

              return (
                <div key={quiz.actId} className="actions-surveys__quiz-item">
                  <div className="actions-surveys__quiz-header" onClick={toggleQuiz} style={{ cursor: "pointer" }}>
                    <h3 className="actions-surveys__quiz-question">
                      {translate("v2_surveys_quiz_question", lang, {
                        question: quiz.questionText || translate("v2_surveys_quiz_default_question", lang),
                      })}
                    </h3>
                    <div className="actions-surveys__quiz-meta">
                      <span className="actions-surveys__quiz-arrow">{isExpanded ? "▼" : "▶"}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="actions-surveys__quiz-content">
                      <div className="actions-surveys__quiz-chart-wrapper">
                        <div className="actions-surveys__quiz-chart">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius="80%"
                                fill="#8884d8"
                                dataKey="value"
                                isAnimationActive={false}
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  fontSize: "14px",
                                  padding: "6px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #e0e0e0",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                                  lineHeight: 1.2,
                                }}
                                itemStyle={{
                                  fontSize: "14px",
                                  padding: 0,
                                  margin: 0,
                                  lineHeight: 1.2,
                                }}
                                formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, props.payload.name]}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="actions-surveys__quiz-answers">
                          <div className="actions-surveys__quiz-legend-participation">
                            <img src={userIcon} alt={translate("v2_surveys_user_alt", lang)} className="actions-surveys__quiz-legend-participation-icon" />
                            <span className="actions-surveys__quiz-legend-participation-text">
                              <span className="actions-surveys__quiz-legend-participation-count">({totalResponses}/{totalStays})</span>
                              <span className="actions-surveys__quiz-legend-participation-pct">{participationPct}%</span>
                            </span>
                          </div>
                          {quiz.answers.map((answer, idx) => (
                            <div key={idx} className="actions-surveys__quiz-answer">
                              <span className="actions-surveys__quiz-answer-dot" style={{ backgroundColor: pieColors[idx % pieColors.length] }}></span>
                              <span className="actions-surveys__quiz-answer-percentage">{answer.percentage}%</span>
                              <span className="actions-surveys__quiz-answer-text">
                                ({answer.timesChosen}) {answer.text || translate("v2_surveys_answer_fallback", lang, { position: answer.position })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="actions-surveys__period-card actions-surveys__period-card--review">
        <h2 className="actions-surveys__card-title">{translate("v2_actions_surveys_review_period", lang)}</h2>
        <p className="actions-surveys__card-subtitle">{translate("v2_actions_surveys_weighted_week", lang)}</p>
        <div className="actions-surveys__period-chart actions-surveys__period-chart--with-legend">
          <div className="actions-surveys__period-chart-area">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mockReviewPeriodData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="0" vertical={false} horizontal={true} stroke="rgba(27, 36, 27, 0.4)" strokeWidth={1} />
                <XAxis dataKey="day" interval={0} tickMargin={8} tick={{ fontSize: 13, fill: "#707070" }} />
                <YAxis domain={[0, 20]} tick={{ fontSize: 13, fill: "#707070" }} width={30} tickLine={false} />
                <Tooltip />
                <Bar dataKey="Label Clef verte" fill="#3C5544" stackId="a" />
                <Bar dataKey="Action 2" fill="#96B77A" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="actions-surveys__period-legend" aria-label={translate("v2_surveys_legend_aria", lang)}>
            <div className="actions-surveys__legend-item">
              <span className="actions-surveys__legend-dot" style={{ backgroundColor: "#3C5544" }} />
              <span>Label Clef verte</span>
            </div>
            <div className="actions-surveys__legend-item">
              <span className="actions-surveys__legend-dot" style={{ backgroundColor: "#96B77A" }} />
              <span>Action 2</span>
            </div>
          </div>
        </div>
      </div>

      <div className="actions-surveys__period-card actions-surveys__period-card--quiz">
        <h2 className="actions-surveys__card-title">{translate("v2_actions_surveys_quiz_period", lang)}</h2>
        <p className="actions-surveys__card-subtitle">{translate("v2_actions_surveys_weighted_week", lang)}</p>
        <div className="actions-surveys__period-chart actions-surveys__period-chart--with-legend">
          <div className="actions-surveys__period-chart-area">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mockQuizPeriodData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="0" vertical={false} horizontal={true} stroke="rgba(27, 36, 27, 0.4)" strokeWidth={1} />
                <XAxis dataKey="day" interval={0} tickMargin={8} tick={{ fontSize: 13, fill: "#707070" }} />
                <YAxis domain={[0, 20]} tick={{ fontSize: 13, fill: "#707070" }} width={30} tickLine={false} />
                <Tooltip />
                <Bar dataKey="Quiz 1" fill="#C68A6A" stackId="a" />
                <Bar dataKey="Quiz 2" fill="#CFA7A0" stackId="a" />
                <Bar dataKey="Quiz 3" fill="#F4EDDE" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="actions-surveys__period-legend" aria-label={translate("v2_surveys_legend_aria", lang)}>
            <div className="actions-surveys__legend-item">
              <span className="actions-surveys__legend-dot" style={{ backgroundColor: "#C68A6A" }} />
              <span>Quiz 1</span>
            </div>
            <div className="actions-surveys__legend-item">
              <span className="actions-surveys__legend-dot" style={{ backgroundColor: "#CFA7A0" }} />
              <span>Quiz 2</span>
            </div>
            <div className="actions-surveys__legend-item">
              <span className="actions-surveys__legend-dot" style={{ backgroundColor: "#F4EDDE" }} />
              <span>Quiz 3</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionsSurveysSections;
